/* global bootstrap */

import React, { useContext, useEffect, useRef } from "react";
import moment from "moment-timezone";
import axiosInstance from "../../utils/Requests/axios";
import toast from "react-hot-toast";
import LanguageContext from "../../utils/Contexts/LanguageContext";
import UserContext from "../../utils/Contexts/UserContext";
import fileDownload from "js-file-download";
import { useNavigate } from "react-router-dom";
import ThemeContext from "../../utils/Contexts/ThemeContext";

import "./style.css"

function ImportTable({
  importData,
  setImportData,
  columnsVisibility,
  setColumnSorts,
  columnSorts,
}) {
  const navigate = useNavigate();
  const { t } = useContext(LanguageContext);
  const { userData } = useContext(UserContext);
  const { selectedTheme } = useContext(ThemeContext);

  const tooltipInit = useRef(false);
  useEffect(() => {
    if (!tooltipInit.current && importData?.length > 0) {
      const tooltipTriggerList = document.querySelectorAll(
        '[data-bs-toggle="tooltip"]'
      );
      [...tooltipTriggerList].forEach(
        (tooltipTriggerEl) => new bootstrap.Tooltip(tooltipTriggerEl)
      );

      tooltipInit.current = true;
    }
  }, [importData]);

  const setColumnSort = (columnIndex) => {
    const columnSortData = { ...columnSorts };
    if (!columnSortData[columnIndex]) columnSortData[columnIndex] = "asc";
    else if (columnSortData[columnIndex] === "asc")
      columnSortData[columnIndex] = "desc";
    else if (columnSortData[columnIndex] === "desc")
      columnSortData[columnIndex] = null;
    setColumnSorts(columnSortData);
  };

  const deleteImports = (productId, importUuid) => {
    const url = `/imports/products/${productId}/sellers/${userData?.uuid}/imports/${importUuid}`;

    axiosInstance
      .delete(url)
      .then(() => {
        const filteredImportData = importData.filter(
          (importItem) => importItem.uuid !== importUuid
        );
        setImportData(filteredImportData);
      })
      .catch(() => {
        toast.error(t(`There was an error deleting Import`));
      });
  };

  const downloadImports = (productId, importUuid, importFilename) => {
    const url = `/imports/products/${productId}/sellers/${userData?.uuid}/imports/${importUuid}/file`;

    axiosInstance
      .get(url, {
        responseType: "blob",
      })
      .then((response) => {
        fileDownload(response.data, importFilename);
      })
      .catch((error) => {
        console.log(error);
        toast.error(t(`There was an error downloading Import`));
      });
  };

  const navigateToOpenDetails = (item) => {
    navigate(`/offers/import/${item?.file_name}`, { state: { item } });
  };

  const formatDate = (date) => {
    if (!date) return "";
    const cleanDate = moment.utc(date);
    const now = moment.utc();
    const isRecent = now.diff(cleanDate, "minutes") <= 30;
    if (isRecent) {
      return cleanDate.fromNow();
    }
    return cleanDate.local().format("MMM DD, YYYY");
  };

  return (
    <div className="table-responsive datatable-custom">
      <table
        id="datatable"
        className="table table-lg table-borderless table-thead-bordered table-nowrap table-align-middle card-table"
      >
        <thead className="thead-light">
          <tr>
            {columnsVisibility.file && (
              <th
                className={`sorting${
                  !columnSorts[0]
                    ? ""
                    : columnSorts[0] === "asc"
                    ? "_asc"
                    : "_desc"
                }`}
                onClick={() => setColumnSort(0)}
              >
                {t("File")}
              </th>
            )}
            {columnsVisibility.date && (
              <th
                className={`sorting${
                  !columnSorts[1]
                    ? ""
                    : columnSorts[1] === "asc"
                    ? "_asc"
                    : "_desc"
                }`}
                onClick={() => setColumnSort(1)}
              >
                {t("Date")}
              </th>
            )}
            {columnsVisibility.product && (
              <th
                className={`sorting${
                  !columnSorts[2]
                    ? ""
                    : columnSorts[2] === "asc"
                    ? "_asc"
                    : "_desc"
                }`}
                onClick={() => setColumnSort(2)}
              >
                {t("Product")}
              </th>
            )}
            {columnsVisibility.type && (
              <th
                className={`sorting${
                  !columnSorts[3]
                    ? ""
                    : columnSorts[3] === "asc"
                    ? "_asc"
                    : "_desc"
                }`}
                onClick={() => setColumnSort(3)}
              >
                {t("Type")}
              </th>
            )}

            {columnsVisibility.status && (
              <th
                className={`sorting${
                  !columnSorts[4]
                    ? ""
                    : columnSorts[4] === "asc"
                    ? "_asc"
                    : "_desc"
                }`}
                onClick={() => setColumnSort(4)}
              >
                {t("Status")}
              </th>
            )}

            {columnsVisibility.imports && (
              <th className="col-11">{t("Imported / Failed")}</th>
            )}
            {columnsVisibility.items && (
              <th
                className={`sorting${
                  !columnSorts[5]
                    ? ""
                    : columnSorts[5] === "asc"
                    ? "_asc"
                    : "_desc"
                }`}
                onClick={() => setColumnSort(5)}
              >
                {t("Items")}
              </th>
            )}
            {columnsVisibility.details && <th>{t("Details")}</th>}
            <th className="table-column-pe-0"></th>
          </tr>
        </thead>

        <tbody>
          {!!importData?.length ? (
            importData?.map((item, index) => (
              <tr key={index}>
                {columnsVisibility.file && (
                  <td
                    className="table-column-ps-1"
                    onClick={() => {
                      if (
                        item.uploaded > 0 &&
                        (item.imported / item.uploaded) * 100 > 0
                      ) {
                        navigateToOpenDetails(item);
                      }
                    }}
                  >
                    <span
                      className={`d-flex align-items-center ${
                        item.uploaded > 0 &&
                        (item.imported / item.uploaded) * 100 > 0
                          ? "cursor-pointer"
                          : ""
                      }`}
                    >
                      <div className="flex-grow-1">
                        <span className="h5 text-inherit">
                          {item?.file_name}
                        </span>
                      </div>
                    </span>
                  </td>
                )}
                {columnsVisibility.date && (
                  <td>
                    <span
                      className="text-dark"
                      data-bs-toggle="tooltip"
                      data-bs-placement="top"
                      title={formatDate(item?.created)}
                    >
                      {formatDate(item?.created)}
                    </span>
                  </td>
                )}
                {columnsVisibility.product && (
                  <td>
                    <span
                      className="badge bg-soft-secondary text-secondary p-2 fs-6"
                      style={{
                        width: "130px",
                      }}
                    >
                      {item?.product === 100 && t("Backlinks Factory")}
                    </span>
                  </td>
                )}
                {columnsVisibility.type && (
                  <td>
                    <span
                      className="badge bg-soft-secondary text-secondary p-2 fs-6"
                      style={{
                        width: "120px",
                      }}
                    >
                      {item?.type === 101
                        ? t("Backlinks Terms")
                        : item?.type === 102
                        ? t("Backlinks Offers")
                        : ""}
                    </span>
                  </td>
                )}
                {columnsVisibility.status && (
                  <td>
                    {item?.status === 100 ? (
                      <span
                        className="badge bg-soft-secondary text-secondary py-2 fs-6"
                        style={{
                          width: "140px",
                        }}
                      >
                        {t("Pending")}
                      </span>
                    ) : item?.status === 101 ? (
                      <span
                        className="badge bg-soft-primary text-primary py-2 fs-6"
                        style={{
                          width: "140px",
                        }}
                      >
                        {t("Processing")}
                      </span>
                    ) : item?.status === 102 ? (
                      <span
                        className="badge bg-soft-success text-success py-2 fs-6"
                        style={{
                          width: "140px",
                        }}
                      >
                        {t("Completed")}
                      </span>
                    ) : (
                      ""
                    )}
                  </td>
                )}

                {columnsVisibility.imports && (
                  <td>
                    <div className="d-flex align-items-center">
                      <div
                        className="progress table-progress px-0"
                        style={{ height: "28px", width: "300px" }}
                      >
                        <div
                          className="progress-bar bg-primary rounded-end"
                          role="progressbar"
                          style={{
                            width: `${
                              item.uploaded > 0
                                ? Math.round(item.imported / item.uploaded) *
                                  100
                                : 0
                            }%`,
                            zIndex: 2,
                            marginRight:
                              item?.failed === item?.uploaded ? "0px" : "-6px",
                          }}
                          aria-valuenow={item.imported}
                          aria-valuemin="0"
                          aria-valuemax={item.uploaded}
                        >
                          <span className="text-start ps-2 fs-6">
                            {item.uploaded > 0
                              ? `${Math.round(
                                  (item.imported / item.uploaded) * 100
                                )}%`
                              : "0%"}
                          </span>
                        </div>
                        <div
                          className="progress-bar bg-danger"
                          role="progressbar"
                          style={{
                            width: `${
                              item.failed > 0
                                ? Math.round(
                                    (item.failed / item.uploaded) * 100 + 2
                                  )
                                : 0
                            }%`,
                            zIndex: 1,
                          }}
                          aria-valuenow={item.failed}
                          aria-valuemin="0"
                          aria-valuemax={item.uploaded}
                        >
                          <span className="text-start ps-2 fs-6">
                            {item?.failed === item?.uploaded ? "0%" : ""}
                          </span>
                        </div>
                        <div
                          className="progress-bar px-0"
                          role="progressbar"
                          style={{
                            background:
                              selectedTheme === "dark"
                                ? "#2c3337"
                                : "rgba(113,134,157,.1)",
                            width: `${item?.status === 101 ? 102 : 0}%`,
                            marginRight:
                              item?.failed === item?.uploaded ? "-6px" : "0px",
                          }}
                          aria-valuenow={
                            item.uploaded - (item.imported + item.failed)
                          }
                          aria-valuemin="0"
                          aria-valuemax={item.uploaded}
                        >
                          <span className="text-start ps-3 fs-6">
                            {item?.status === 101 ? "0%" : ""}
                          </span>
                        </div>
                      </div>
                    </div>
                  </td>
                )}
                {columnsVisibility.items && <td>{item?.uploaded}</td>}
                {columnsVisibility.details && (
                  <td>
                    <button
                      className="btn btn-outline-primary btn-sm p-2 fs-4 d-flex"
                      type="button"
                      onClick={() => navigateToOpenDetails(item)}
                    >
                      <i className="bi bi-list-ul me-2 fs-4 text-center"></i>
                      <span>{t("Details")}</span>
                    </button>
                  </td>
                )}
                <td className="p-1">
                  <div className="dropdowm">
                    <button
                      type="button"
                      className="btn btn-ghost-secondary btn-icon btn-sm rounded-circle"
                      id="teamsDropdown2"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    >
                      <i className="bi-three-dots-vertical"></i>
                    </button>

                    <div
                      className="dropdown-menu dropdown-menu-sm dropdown-menu-end"
                      aria-labelledby="teamsDropdown2"
                    >
                      <button
                        className="dropdown-item"
                        onClick={() => navigateToOpenDetails(item)}
                      >
                        <i className="bi-list-columns-reverse dropdown-item-icon"></i>
                        {t("See Import Details")}
                      </button>
                      <button
                        className="dropdown-item"
                        onClick={() =>
                          downloadImports(
                            item?.product,
                            item?.uuid,
                            item?.file_name
                          )
                        }
                      >
                        <i className="bi-file-earmark-arrow-down dropdown-item-icon"></i>
                        {t("Download Import File")}
                      </button>
                      <button
                        className="dropdown-item text-danger"
                        onClick={() => deleteImports(item?.product, item?.uuid)}
                      >
                        <i className="bi-x-circle dropdown-item-icon"></i>{" "}
                        {t("Delete")}
                      </button>
                    </div>
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="100%">
                <div
                  className="text-center p-4"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    textAlign: "center",
                  }}
                >
                  <img
                    className="mb-3"
                    src="https://static.articlesfactory.ai/app/svg/illustrations-light/oc-error.svg"
                    alt=""
                    style={{ width: "10rem" }}
                    data-hs-theme-appearance="dark"
                  />
                  <img
                    className="mb-3"
                    src="https://static.articlesfactory.ai/app/svg/illustrations/oc-error.svg"
                    alt=""
                    style={{ width: "10rem" }}
                    data-hs-theme-appearance="default"
                  />
                  <p className="mb-0">
                    {!importData ? t("Loading data") : t("No data to show")}
                  </p>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default ImportTable;
